import {
  Avatar,
  Badge,
  CellHeadingStyle,
  Checkbox,
  type ClientSortValues,
  GREY_800,
  SPACING_0_5,
  SPACING_2,
  type SortableCell,
  Link as UILink,
  withLineClamp,
} from '@mnd-frontend/ui';
import { type Dispatch, type SetStateAction, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import styled from 'styled-components';
import { useCurrentPressroom } from '../../../../../contexts/CurrentPressroomContext';
import type { NetworkContactFragment } from '../../../../../generated/types';
import { track } from '../../../../../lib/tracker';
import getContactName from '../../../../../modules/contacts/getContactName';
import routes from '../../../../../routes';

export type Selected = { contactId: string; name: string }[];

const StyledRowItem = styled.div`
  display: flex;
  align-items: center;

  > *:not(:last-child) {
    margin-right: ${SPACING_2} !important;
  }
`;

const Static = styled.div`
  flex-shrink: 0;
  display: inherit;
  align-items: inherit;
  line-height: 0;
`;

const HeaderWrapper = styled.div`
  display: flex;
  gap: ${SPACING_0_5};
`;

const HeaderLink = styled(UILink)`
  ${withLineClamp(1)}
  ${CellHeadingStyle}
  overflow-wrap: anywhere;
`;

const DescriptionLink = styled(UILink)`
  color: ${GREY_800} !important;
  overflow-wrap: anywhere;
`;

type Row = [SortableCell, ClientSortValues, ClientSortValues];

export interface ContactType extends NetworkContactFragment {
  networkContactLists: {
    id: string;
  }[];
}

const InnerRowItemDetails = ({
  contact,
  name,
  to,
}: {
  contact: ContactType;
  name: string;
  to: string;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Static>
        <UILink to={to} as={Link}>
          <Avatar
            type="contact"
            name={name}
            image={contact?.avatarUrl ?? undefined}
            importedVia={contact?.importedVia}
          />
        </UILink>
      </Static>
      <div>
        <HeaderWrapper>
          <HeaderLink to={to} as={Link}>
            {name}
          </HeaderLink>
          {contact.hasMediaHits && (
            <div>
              <Badge variant="commercial" text={t('common_media-hit')} />
            </div>
          )}
        </HeaderWrapper>
        <DescriptionLink to={to} as={Link}>
          {contact.email}
        </DescriptionLink>
      </div>
    </>
  );
};

const RowItemDetails = memo(InnerRowItemDetails);

const InnerRowItem = ({
  contact,
  checked,
  name,
  to,
  handleChangeChecked,
}: {
  contact: ContactType;
  name: string;
  to: string;
  checked: boolean;
  handleChangeChecked: (contactId: string, e: any) => void;
}) => {
  return (
    <StyledRowItem>
      <Static>
        <Checkbox
          checked={checked}
          onChange={event => {
            track({
              event: 'Contacts: Checked Inactive Contact',
              traits: { checked: event.target.checked },
            });
            handleChangeChecked(contact.id, event);
          }}
          id={`checkbox:${contact.id}`}
        />
      </Static>
      <RowItemDetails name={name} to={to} contact={contact} />
    </StyledRowItem>
  );
};

const RowItem = memo(InnerRowItem);

export const useGetRows = (
  contacts: ContactType[],
  selected: { value: Set<string> },
  setSelected: Dispatch<SetStateAction<{ value: Set<string> }>>,
  allSelected: boolean,
): Row[] => {
  const [t] = useTranslation();
  const { pressroomId } = useCurrentPressroom();

  const handleChangeChecked = useCallback(
    (contactId: string, e: any) => {
      if (allSelected) return;

      const { checked } = e.target;

      setSelected(currentSelected => {
        const value = currentSelected.value;
        if (checked) {
          value.add(contactId);
          return { value };
        }
        value.delete(contactId);
        return { value };
      });
    },
    [setSelected, allSelected],
  );

  return (contacts ?? []).map(contact => {
    const name = getContactName(contact, false);

    return [
      [
        <RowItem
          contact={contact}
          name={name}
          to={routes.contactShowWithReturnTo({
            pressroomId,
            id: contact.id,
            returnTo: window.location.pathname,
          })}
          checked={allSelected || selected.value.has(contact.id)}
          handleChangeChecked={handleChangeChecked}
        />,
        name,
      ],
      contact.unreachable ? t('contacts_status_unreachable') : t('contacts_status_unsubscribed'),
      contact.networkContactLists.length > 0
        ? t('contacts_status_num-of-contact-lists', { count: contact.networkContactLists.length })
        : undefined,
    ];
  });
};

const sortValues = [
  {
    wtiKey: 'contacts_sort_name',
  },
  {
    wtiKey: 'contacts_status',
  },
  {
    wtiKey: 'contacts_contact-lists',
  },
];

export const useGetTableHeadings = () => {
  const [t] = useTranslation();

  return sortValues.map(value => ({
    text: t(value.wtiKey),
  }));
};
